import { GameState } from "../state";
import { ENEMIES, type EnemyDef } from "../../data/enemies";
import type { Enemy } from "../entities";

// Time-driven director. Spawn rate and the eligible enemy pool ramp with the
// run timer (wave ~= minute). Enemies appear just outside the camera view.
let spawnAccum = 0;

export function resetSpawner(): void {
  spawnAccum = 0;
}

export function updateSpawner(s: GameState, dt: number): void {
  s.wave = Math.floor(s.time / 60);

  // During a boss fight, stop the regular trickle — the boss summons its own.
  if (s.bossEnemy && s.bossEnemy.alive) return;

  // Spawns per second scales gently with time, capped to keep things sane,
  // then scaled by difficulty.
  const rate = Math.min(1.0 + s.time * 0.03, 8) * s.difficulty.enemyRate;
  spawnAccum += rate * dt;

  // New threats unlock by ZONE, not time — each zone introduces more enemy types.
  const pool = eligiblePool(s.zone - 1);
  const totalWeight = pool.reduce((a, e) => a + e.weight, 0);

  while (spawnAccum >= 1) {
    spawnAccum -= 1;
    const def = weightedPick(s, pool, totalWeight);
    spawnEnemy(s, def);
  }

  // Periodic "Flood" swarm event — unlocked from zone 3 onward.
  if (s.zone >= 3 && Math.floor(s.time) % 60 === 0 && s.time - Math.floor(s.time) < dt) {
    floodEvent(s);
  }
}

function eligiblePool(wave: number): EnemyDef[] {
  return Object.values(ENEMIES).filter((e) => e.minWave <= wave);
}

function weightedPick(s: GameState, pool: EnemyDef[], total: number): EnemyDef {
  let r = s.rng.next() * total;
  for (const e of pool) {
    r -= e.weight;
    if (r <= 0) return e;
  }
  return pool[pool.length - 1];
}

function spawnEnemy(s: GameState, def: EnemyDef): void {
  // Whole arena is visible, so enemies enter from the edges and move inward.
  const { x, y } = perimeterPoint(s);
  // Tankier each zone, plus a small CAPPED time creep (so trash can't out-scale
  // bosses on long runs), then scaled by difficulty.
  const hpScale = (1 + (s.zone - 1) * 0.25 + Math.min(s.wave, 8) * 0.05) * s.difficulty.enemyHp;
  s.enemies.push(makeEnemy(def, x, y, hpScale));
}

function perimeterPoint(s: GameState): { x: number; y: number } {
  const m = 18;
  switch (s.rng.int(0, 3)) {
    case 0:
      return { x: s.rng.range(m, s.arenaW - m), y: m }; // top
    case 1:
      return { x: s.arenaW - m, y: s.rng.range(m, s.arenaH - m) }; // right
    case 2:
      return { x: s.rng.range(m, s.arenaW - m), y: s.arenaH - m }; // bottom
    default:
      return { x: m, y: s.rng.range(m, s.arenaH - m) }; // left
  }
}

export function makeEnemy(def: EnemyDef, x: number, y: number, hpScale = 1): Enemy {
  const hp = def.hp * hpScale;
  return {
    alive: true,
    def,
    x,
    y,
    vx: 0,
    vy: 0,
    hp,
    maxHp: hp,
    radius: def.radius,
    speed: def.speed,
    hitFlash: 0,
    cooldown: 0,
    buffed: 0,
    carrying: 0,
  };
}

function floodEvent(s: GameState): void {
  const def = ENEMIES.worm;
  const count = 20 + s.wave * 5;
  s.addFloat(s.player.x, s.player.y - 80, "FLOOD INCOMING", "#ff5189", 26);
  for (let i = 0; i < count; i++) {
    const { x, y } = perimeterPoint(s);
    s.enemies.push(makeEnemy(def, x, y, 1));
  }
}
